import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'
import { toast } from '@/stores/useToastStore'
import { PlanInfoTab } from './PlanInfoTab'
import { PlanHistoryTab } from './PlanHistoryTab'
import { TransferOwnershipModal } from './TransferOwnershipModal'
import type { ApiPlanDetailResponse } from '@/types'

type TabType = 'info' | 'history'

interface PlanInformationDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultTab?: TabType
  planId: string
  planMetadata?: ApiPlanDetailResponse
}

export function PlanInformationDrawer({
  open,
  onOpenChange,
  defaultTab = 'info',
  planId,
  planMetadata,
}: PlanInformationDrawerProps) {
  const [activeTab, setActiveTab] = useState<TabType>(defaultTab)
  const [transferModalOpen, setTransferModalOpen] = useState(false)

  // Update active tab when defaultTab prop changes
  useEffect(() => {
    if (open) {
      setActiveTab(defaultTab)
    }
  }, [defaultTab, open])

  const handleTransfer = () => {
    setTransferModalOpen(false)
    onOpenChange(false)
    toast.success('The assortment plan ownership was successfully transferred')
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          showCloseButton={false}
          className="flex w-[600px] flex-col sm:max-w-[600px]"
        >
          {/* Header */}
          <SheetHeader className="border-b px-6 py-4">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-lg font-semibold">
                Plan Information
              </SheetTitle>
              <SheetClose asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 cursor-pointer"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </Button>
              </SheetClose>
            </div>

            {/* Tab toggle */}
            <div className="mt-4 h-[40px] w-fit  rounded-[10px] bg-accent px-2 mx-2 pt-1">
              <div className="flex">
                <button
                  type="button"
                  onClick={() => setActiveTab('info')}
                  className={cn(
                    'w-[57px] shrink-0 cursor-pointer rounded-lg px-4 py-1.5 text-sm font-medium transition-all',
                    activeTab === 'info'
                      ? 'bg-primary text-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.1)]'
                      : 'text-primary hover:bg-white/50'
                  )}
                >
                  Info
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('history')}
                  className={cn(
                    'flex-1 cursor-pointer rounded-lg px-4 py-1.5 text-sm font-medium transition-all',
                    activeTab === 'history'
                      ? 'bg-primary text-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.1)]'
                      : 'text-primary hover:bg-white/50'
                  )}
                >
                  History
                </button>
              </div>
            </div>
          </SheetHeader>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {activeTab === 'info' ? (
              <PlanInfoTab
                planId={planId}
                planMetadata={planMetadata}
                onTransferClick={() => setTransferModalOpen(true)}
              />
            ) : (
              <PlanHistoryTab planId={planId} />
            )}
          </div>

          {/* Footer */}
          <SheetFooter className="border-t px-6 py-4">
            <SheetClose asChild>
              <Button variant="outline" className="w-full cursor-pointer">
                Close
              </Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <TransferOwnershipModal
        open={transferModalOpen}
        onOpenChange={setTransferModalOpen}
        onTransfer={handleTransfer}
        planId={planId}
      />
    </>
  )
}
